const API_BASE = ''

class ApiService {
    constructor() {
        this._redirecting = false
    }

    getToken() {
        const user = localStorage.getItem('jt_user')
        if (user) {
            try {
                const parsed = JSON.parse(user)
                return parsed.token
            } catch {
                return null
            }
        }
        return null
    }

    /**
     * Backstop for the idle timer: if a request comes back 401 (token expired
     * server-side, refresh token revoked, etc.) we clear local auth and bounce
     * the user to /login with a friendly message. The idle timer at 2h handles
     * the common case; this catches the edge cases (laptop closed for days,
     * server restart that invalidated tokens, etc.).
     */
    handleUnauthorized() {
        if (this._redirecting) return
        // Already on login page � don't redirect-loop on a failed login attempt.
        if (typeof window !== 'undefined' && window.location.pathname === '/login') return

        this._redirecting = true
        sessionStorage.setItem('jt_auth_message', 'Your session expired. Please log in again.')
        localStorage.removeItem('jt_user')

        // Brief delay so any in-flight responses can finish parsing before the page reloads.
        setTimeout(() => {
            window.location.href = '/login'
        }, 50)
    }

    async request(method, endpoint, data = null, options = {}) {
        const url = `${API_BASE}${endpoint}`
        const token = this.getToken()

        const config = {
            method,
            headers: {
                'Content-Type': 'application/json',
                ...options.headers,
            },
            credentials: 'include',
        }

        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`
        }

        if (data && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
            config.body = JSON.stringify(data)
        }

        try {
            const response = await fetch(url, config)

            // 401 anywhere except the login endpoints means the session died.
            // Login endpoints return 401 for "wrong password" � those should be
            // handled by the login page's own error display, not redirected.
            if (response.status === 401
                && !endpoint.includes('/api/auth/login')) {
                this.handleUnauthorized()
            }

            const contentType = response.headers.get('content-type')
            if (contentType && contentType.includes('application/json')) {
                const json = await response.json()
                return json
            } else {
                const text = await response.text()
                return { success: response.ok, data: text }
            }
        } catch (error) {
            console.error('API request failed:', error)
            throw error
        }
    }

    get(endpoint, options = {}) {
        return this.request('GET', endpoint, null, options)
    }

    post(endpoint, data, options = {}) {
        return this.request('POST', endpoint, data, options)
    }

    put(endpoint, data, options = {}) {
        return this.request('PUT', endpoint, data, options)
    }

    patch(endpoint, data, options = {}) {
        return this.request('PATCH', endpoint, data, options)
    }

    delete(endpoint, options = {}) {
        return this.request('DELETE', endpoint, null, options)
    }
}

export const api = new ApiService()

// Timesheet API
export const timesheetApi = {
    getAll: (filters = {}) => {
        const params = new URLSearchParams(filters).toString()
        return api.get(`/api/timesheets${params ? `?${params}` : ''}`)
    },
    getById: (uuid) => api.get(`/api/timesheets/${uuid}`),
    create: (data) => api.post('/api/timesheets', data),
    update: (uuid, data) => api.put(`/api/timesheets/${uuid}`, data),
    delete: (uuid) => api.delete(`/api/timesheets/${uuid}`),
    submit: (uuid) => api.post(`/api/timesheets/${uuid}/submit`),
    returnToDraft: (uuid) => api.post(`/api/timesheets/${uuid}/return-to-draft`),
    saveSignature: (uuid, signatureData) => api.post(`/api/timesheets/${uuid}/signature`, { signature: signatureData }),
    saveSketch: (uuid, sketchData) => api.post(`/api/timesheets/${uuid}/sketch`, { sketch: sketchData }),

    // Packing Slip Items
    getPackingSlipItems: (uuid) => api.get(`/api/timesheets/${uuid}/packing-slip-items`),
    addPackingSlipItem: (uuid, data) => api.post(`/api/timesheets/${uuid}/packing-slip-items`, data),
    updatePackingSlipItem: (itemUuid, data) => api.put(`/api/timesheets/packing-slip-items/${itemUuid}`, data),
    deletePackingSlipItem: (itemUuid) => api.delete(`/api/timesheets/packing-slip-items/${itemUuid}`),

    // Pickup Slip Items
    getPickupSlipItems: (uuid) => api.get(`/api/timesheets/${uuid}/pickup-slip-items`),
    addPickupSlipItem: (uuid, data) => api.post(`/api/timesheets/${uuid}/pickup-slip-items`, data),
    updatePickupSlipItem: (itemUuid, data) => api.put(`/api/timesheets/pickup-slip-items/${itemUuid}`, data),
    deletePickupSlipItem: (itemUuid) => api.delete(`/api/timesheets/pickup-slip-items/${itemUuid}`),

    // Vendor Slip Items
    getVendorSlipItems: (uuid) => api.get(`/api/timesheets/${uuid}/vendor-slip-items`),
    addVendorSlipItem: (uuid, data) => api.post(`/api/timesheets/${uuid}/vendor-slip-items`, data),
    updateVendorSlipItem: (itemUuid, data) => api.put(`/api/timesheets/vendor-slip-items/${itemUuid}`, data),
    deleteVendorSlipItem: (itemUuid) => api.delete(`/api/timesheets/vendor-slip-items/${itemUuid}`),

    // Parts Used
    getParts: (uuid) => api.get(`/api/timesheets/${uuid}/parts`),
    addPart: (uuid, data) => api.post(`/api/timesheets/${uuid}/parts`, data),
    updatePart: (partUuid, data) => api.put(`/api/timesheets/parts/${partUuid}`, data),
    deletePart: (partUuid) => api.delete(`/api/timesheets/parts/${partUuid}`),

    // Drawings / Sketches
    getDrawings: (referenceNo) => api.get(`/api/timesheets/drawings?referenceNo=${encodeURIComponent(referenceNo)}`),
    getDrawing: (drawingId) => api.get(`/api/timesheets/drawings/${drawingId}`),
    saveDrawing: (data) => api.post('/api/timesheets/drawings', data),
    deleteDrawing: (drawingId) => api.delete(`/api/timesheets/drawings/${drawingId}`),
}

// Job API
export const jobApi = {
    getAll: () => api.get('/api/jobs'),
    getByWorkOrder: (workOrder) => api.get(`/api/jobs/${workOrder}`),
}

// Customer API
export const customerApi = {
    getAll: () => api.get('/api/customers'),
    getById: (id) => api.get(`/api/customers/${id}`),
    create: (data) => api.post('/api/customers', data),
    update: (id, data) => api.put(`/api/customers/${id}`, data),
    delete: (id) => api.delete(`/api/customers/${id}`),
}

// User API
export const userApi = {
    getAll: () => api.get('/api/users'),
    getById: (id) => api.get(`/api/users/${id}`),
    create: (data) => api.post('/api/users', data),
    update: (id, data) => api.put(`/api/users/${id}`, data),
    delete: (id) => api.delete(`/api/users/${id}`),
}

// Inventory API
export const inventoryApi = {
    getAll: () => api.get('/api/inventory'),
    getById: (id) => api.get(`/api/inventory/${id}`),
    search: (query) => api.get(`/api/inventory/search?q=${encodeURIComponent(query)}`),
    create: (data) => api.post('/api/inventory', data),
    update: (id, data) => api.put(`/api/inventory/${id}`, data),
    updateQuantity: (id, quantity) => api.patch(`/api/inventory/${id}/quantity`, { quantity }),
    delete: (id) => api.delete(`/api/inventory/${id}`),

    // Per-unit serial tracking
    getUnits: (id) => api.get(`/api/inventory/${id}/units`),
    addUnits: (id, data) => api.post(`/api/inventory/${id}/units`, data),
    removeUnits: (id, data) => api.post(`/api/inventory/${id}/units/remove`, data),
    lookupSerial: (serial) => api.get(`/api/inventory/units/lookup?serial=${encodeURIComponent(serial)}`),

    getLocations: () => api.get('/api/inventory/locations'),

    // Returns a PDF, not JSON, so it bypasses the shared request() helper and
    // hands back a blob the caller can turn into a download.
    downloadReport: async (options) => {
        const token = api.getToken()
        const response = await fetch('/api/inventory/report', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            credentials: 'include',
            body: JSON.stringify(options),
        })

        if (!response.ok) {
            throw new Error(`Report failed (${response.status})`)
        }

        const disposition = response.headers.get('content-disposition') || ''
        const match = disposition.match(/filename[^;=\n]*=(?:UTF-8'')?"?([^";\n]*)"?/i)
        const filename = match ? decodeURIComponent(match[1]) : 'inventory.pdf'

        return { blob: await response.blob(), filename }
    },
}

// Expense API
export const expenseApi = {
    // Categories
    getCategories: (includeInactive = false) =>
        api.get(`/api/expenses/categories${includeInactive ? '?includeInactive=true' : ''}`),
    createCategory: (data) => api.post('/api/expenses/categories', data),
    updateCategory: (id, data) => api.put(`/api/expenses/categories/${id}`, data),
    deactivateCategory: (id) => api.delete(`/api/expenses/categories/${id}`),
    // The whole order at once - see ReorderCategoriesAsync for why it is not a swap.
    reorderCategories: (ids) => api.put('/api/expenses/categories/order', { ids }),

    // The employee's own entries
    getMine: (month) => api.get(`/api/expenses/mine?month=${encodeURIComponent(month)}`),

    // Bank of Canada rate for a foreign receipt's date, for the CAD preview.
    getExchangeRate: (currency, date) =>
        api.get(`/api/expenses/exchange-rate?${new URLSearchParams({ currency, date }).toString()}`),
    create: (data) => api.post('/api/expenses', data),

    // Receipts. The image travels as a data URL, the same shape the signature and
    // sketch endpoints already use; the server decodes it and writes a file.
    saveReceipt: (uuid, dataUrl) => api.post(`/api/expenses/${uuid}/receipt`, { dataUrl }),

    // The receipt endpoint is [Authorize], and a plain <a href> navigation sends
    // no Authorization header - the token only rides along on fetches made
    // through this helper. So the bytes are fetched here and handed back as an
    // object URL for the viewer to render. Caller must revokeObjectURL when done.
    fetchReceipt: async (uuid) => {
        const token = api.getToken()
        const response = await fetch(`/api/expenses/${uuid}/receipt`, {
            headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            credentials: 'include',
        })

        if (!response.ok) {
            if (response.status === 404) throw new Error('No receipt is stored for this expense.')
            if (response.status === 401) throw new Error('Your session has expired � log in again.')
            if (response.status === 403) throw new Error('You do not have permission to view this receipt.')
            throw new Error(`Could not load the receipt (${response.status})`)
        }

        const blob = await response.blob()
        return { url: URL.createObjectURL(blob), type: blob.type, size: blob.size }
    },

    // Reads the receipt and suggests form values. Saves nothing. Returns
    // available:false when no API key is configured, in which case the form
    // simply stays manual.

    // Manager views
    getForEmployee: (employee, month, card) => {
        const params = new URLSearchParams({ employee, month })
        if (card) params.set('card', card)
        return api.get(`/api/expenses?${params.toString()}`)
    },
    getEmployees: (month) => api.get(`/api/expenses/employees?month=${encodeURIComponent(month)}`),
    update: (uuid, data) => api.put(`/api/expenses/${uuid}`, data),
    delete: (uuid) => api.delete(`/api/expenses/${uuid}`),
    getReportHistory: () => api.get('/api/expenses/reports'),

    // Returns a PDF rather than JSON, so it bypasses the shared request() helper
    // and hands back a blob the caller can turn into a download.
    downloadReport: async (options) => {
        const token = api.getToken()
        const response = await fetch('/api/expenses/report', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            credentials: 'include',
            body: JSON.stringify(options),
        })

        if (!response.ok) {
            // The server explains an empty range in JSON; surface that rather
            // than a bare status code.
            let message = `Report failed (${response.status})`
            try {
                const problem = await response.json()
                if (problem?.message) message = problem.message
            } catch {
                // response was not JSON, keep the status message
            }
            throw new Error(message)
        }

        const disposition = response.headers.get('content-disposition') || ''
        const match = disposition.match(/filename[^;=\n]*=(?:UTF-8'')?"?([^";\n]*)"?/i)
        const filename = match ? decodeURIComponent(match[1]) : 'expense-report.pdf'

        return { blob: await response.blob(), filename }
    },
}

// Sync API
export const syncApi = {
    getStatus: () => api.get('/api/sync/status'),
    trigger: (commandType, payload = null) => api.post('/api/sync/trigger', { commandType, payload }),
    getPendingTimesheets: () => api.get('/api/sync/pending-timesheets'),
    getJobTimesheets: (workOrderNumber) => api.get(`/api/sync/job-timesheets?workOrderNumber=${encodeURIComponent(workOrderNumber)}`),
    getExportedTimesheets: (jobNumber) => api.get(`/api/sync/exported-timesheets?jobNumber=${encodeURIComponent(jobNumber)}`),
    rollbackTimesheets: (workOrderNumber, uuids, referenceNumbers) => api.post('/api/sync/rollback', { WorkOrderNumber: workOrderNumber, Uuids: uuids, ReferenceNumbers: referenceNumbers }),
    getLogs: () => api.get('/api/sync/logs'),
}

// QBO API
export const qboApi = {
    getStatus: () => api.get('/api/qbo/status'),
    connect: () => api.post('/api/qbo/connect'),
    disconnect: () => api.post('/api/qbo/disconnect'),
    syncCustomers: () => api.post('/api/qbo/sync/customers'),
    syncEstimates: () => api.post('/api/qbo/sync/estimates'),
    syncEmployees: () => api.post('/api/qbo/sync/employees'),
    syncAll: () => api.post('/api/qbo/sync/all'),
    exportTimesheets: (uuids) => api.post('/api/qbo/export', { uuids }),
    getActiveEmployees: () => api.get('/api/qbo/employees/active'),
}

// Company Settings API
export const companyApi = {
    getSettings: () => api.get('/api/company-settings'),
    updateSettings: (data) => api.put('/api/company-settings', data),
    uploadLogo: (side, file) => {
        const formData = new FormData()
        formData.append('file', file)
        const token = api.getToken()
        return fetch(`/api/company-settings/logo/${side}`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData
        }).then(res => res.json())
    },
    deleteLogo: (side) => api.delete(`/api/company-settings/logo/${side}`),

    // The vehicle plates behind the picker on a fuel expense. Reading is open to
    // anyone signed in; saving is sysadmin-only and replaces the whole list.
    getPlates: () => api.get('/api/company-settings/plates'),
    updatePlates: (plates) => api.put('/api/company-settings/plates', { plates }),
}